import { Injectable } from '@nestjs/common';
import { Prisma } from '@prisma/client';
import { financialYearLabel } from './financial-year';

export type InvoiceKind = 'tax_invoice' | 'credit_note';

/** Tax invoices and credit notes are separate runs and must look different. */
const PREFIX: Record<InvoiceKind, string> = {
  tax_invoice: 'INV',
  credit_note: 'CRN',
};

export interface AllocatedNumber {
  serial: number;
  invoiceNumber: string;
}

/**
 * Allocates the next number in a store's series.
 *
 * Deliberately NOT a Postgres sequence. `nextval()` is exempt from rollback by
 * design, so every failed transaction burns a number and leaves a hole — and
 * Rule 46 of the CGST Rules wants a *consecutive* serial, unique within the
 * financial year. An auditor asking why 1042 is missing is not a conversation
 * worth having to save a row lock.
 *
 * A counter row does roll back, because `UPDATE … RETURNING` is part of the
 * caller's transaction. The row lock serialises concurrent issuers for the same
 * store, which is exactly right: two invoices must never share a number, and
 * waiting a few milliseconds is cheaper than any reconciliation.
 *
 * MUST be called inside the transaction that inserts the invoice, and as late
 * as possible in it — anything that can fail should have failed already.
 */
@Injectable()
export class InvoiceNumberService {
  async allocate(
    tx: Prisma.TransactionClient,
    storeId: string,
    financialYear: number,
    kind: InvoiceKind
  ): Promise<AllocatedNumber> {
    // The counter may not exist yet; creating it is idempotent and safe to race.
    await tx.invoiceCounter.upsert({
      where: { storeId_financialYear_kind: { storeId, financialYear, kind } },
      update: {},
      create: { storeId, financialYear, kind, nextNumber: 1 },
    });

    const counter = await tx.invoiceCounter.update({
      where: { storeId_financialYear_kind: { storeId, financialYear, kind } },
      data: { nextNumber: { increment: 1 } },
      select: { nextNumber: true },
    });

    // `update` returns the NEW value, so the number just handed out is one less.
    const serial = counter.nextNumber - 1;

    return {
      serial,
      invoiceNumber: [
        PREFIX[kind],
        financialYearLabel(financialYear),
        String(serial).padStart(8, '0'),
      ].join('/'),
    };
  }
}
